import Swal from 'sweetalert2';
import BurnerAnalysisService from './BurnerAnalysisService';

export type RunTarget =
    | { type: 'institute'; id: number | string; name: string; totalAlumni?: number }
    | { type: 'alumni'; id: string; name: string; instituteId: number | string };

interface Props {
    target: RunTarget;
    onClose: () => void;
    onCompleted: () => void;
}

const RunAnalysisModal = ({ target, onClose, onCompleted }: Props) => {
    const isInstitute = target.type === 'institute';

    const handleConfirm = async () => {
        onClose();
        Swal.fire({
            title: 'Starting burner analysis…',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
        });

        try {
            const res =
                target.type === 'institute'
                    ? await BurnerAnalysisService.triggerInstituteRun(target.id)
                    : await BurnerAnalysisService.triggerAlumniRun(target.id, target.instituteId);

            const data = (res && (res as any).data) || {};
            if (data.success === false) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Something went wrong',
                    text: data.message || undefined,
                });
                return;
            }

            await Swal.fire({
                icon: 'success',
                title: 'Burner analysis queued',
                text:
                    target.type === 'institute'
                        ? `Analysis started for all alumni of ${target.name}.`
                        : `Analysis started for ${target.name}.`,
            });
            onCompleted();
        } catch (_err) {
            await Swal.fire({ icon: 'error', title: 'Something went wrong' });
        }
    };

    return (
        <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(15,23,42,0.45)' }}>
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {isInstitute ? 'Run burner analysis — institute' : 'Run burner analysis — profile'}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>
                    <div className="modal-body">
                        {isInstitute ? (
                            <p>
                                This will queue burner analysis for all{' '}
                                <strong>{Number(target.totalAlumni ?? 0).toLocaleString()}</strong> alumni profiles of{' '}
                                <strong>{target.name}</strong>.
                            </p>
                        ) : (
                            <p>
                                This will run burner analysis for <strong>{target.name}</strong>'s profile.
                            </p>
                        )}
                        <div
                            className="p-3 rounded"
                            style={{ background: '#faeeda', border: '0.5px solid #fac775', color: '#633806' }}>
                            {isInstitute
                                ? 'LinkedIn activity files should be pre-loaded to S3 before starting. Missing files will be flagged in data source status but will not stop the analysis.'
                                : "If this alumni's LinkedIn activity file is not found in S3, the analysis will proceed without it and the SM status will show N."}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-light" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleConfirm}>
                            Confirm &amp; start
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RunAnalysisModal;
